// src/pages/shop/ShopCounterpartyCard.jsx
//
// Карточка одного контрагента (поставщик или оптовый клиент или оба сразу).
// Три вкладки:
//   - Данные: имя, контакты, кнопка редактировать
//   - Операции: закупки от поставщика (потом — отгрузки оптовику)
//   - Платежи: история платежей в обе стороны + кнопка добавить

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { counterparties as cpApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import { useMyCurrency, useMyShop } from '../../hooks/useMyShop';
import { isModuleOn } from '../../config/modules';
import { useDeletePin } from '../../hooks/useDeletePin';
import { formatMoney } from '../../data/currencies';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import SafeInput from '../../components/SafeInput';
import CounterpartyEditSheet from '../../components/CounterpartyEditSheet';
import PaymentSheet from '../../components/PaymentSheet';
import { useShopNav } from './ShopHome';

const API_HOST = (() => {
  try {
    const u = new URL(process.env.REACT_APP_API_URL || 'https://api.alsatuu.biz/api');
    return `${u.protocol}//${u.host}`;
  } catch { return 'https://api.alsatuu.biz'; }
})();
function fullUrl(u) {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return API_HOST + u;
}

export default function ShopCounterpartyCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const navItems = useShopNav();
  const { show, ToastView } = useToast();
  const { confirm: confirmDelete, PinModal } = useDeletePin();
  const currency = useMyCurrency();
  const myShop = useMyShop();

  const [tab, setTab] = useState('data');                 // 'data' | 'operations' | 'payments'
  const [summary, setSummary] = useState(null);
  const [operations, setOperations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [paymentDirection, setPaymentDirection] = useState(null); // 'outgoing'|'incoming'|null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o, p] = await Promise.all([
        cpApi.summary(id),
        cpApi.operations(id),
        cpApi.payments(id),
      ]);
      setSummary(s);
      setOperations(o.operations || []);
      setPayments(p.items || []);
    } catch (e) {
      show(e.message || t('common.error'), 'error');
    } finally {
      setLoading(false);
    }
  }, [id, show, t]);

  useEffect(() => { load(); }, [load]);

  if (loading || !summary) {
    return (
      <div className="page">
        <div className="loader" style={{ padding: 64 }}>
          <div className="spinner" />
        </div>
        <BottomNav items={navItems} />
      </div>
    );
  }

  const cp = summary.counterparty;
  const isSupplier  = cp.isSupplier;
  const isWholesale = cp.isWholesaleCustomer;

  const onEditSaved = (updated) => {
    setEditOpen(false);
    show(t('counterparties.updated'));
    load();
  };

  const onPaymentSaved = () => {
    setPaymentDirection(null);
    show(t('cpPayments.saved'));
    load();
  };

  const onDeletePayment = async (paymentId) => {
    const ok = await confirmDelete({
      title: t('cpPayments.deleteTitle', 'Удалить платёж?'),
      message: t('cpPayments.confirmDelete'),
    });
    if (!ok) return;
    try {
      await cpApi.removePayment(paymentId);
      show(t('cpPayments.deleted'));
      load();
    } catch (e) {
      show(e.message || t('common.error'), 'error');
    }
  };

  return (
    <div className="page">
      {ToastView}
      {PinModal}

      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 className="page-title">{cp.name}</h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Бейджи типов */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {isSupplier && (
            <span style={badgeStyle('var(--brand-primary)', 'var(--brand-primary-soft)')}>
              {t('counterparties.badgeSupplier')}
            </span>
          )}
          {isWholesale && (
            <span style={badgeStyle('var(--success)', 'var(--success-soft)')}>
              {t('counterparties.badgeWholesale')}
            </span>
          )}
          {cp.linkedShop && (
            <span style={badgeStyle('var(--brand-accent)', 'var(--brand-accent-soft)')}>
              ★ {cp.linkedShop.name}
            </span>
          )}
        </div>

        {/* Сводка по долгу — карточка-итог */}
        <div style={summaryCardStyle}>
          {isSupplier && (
            <div style={summaryRow}>
              <div>
                <div style={summaryLabel}>{t('cpPayments.supplierSection')}</div>
                <div style={summaryDebt(summary.supplier.debt)}>
                  {summary.supplier.debt > 0
                    ? t('cpPayments.youOwe') + ' ' + formatMoney(summary.supplier.debt, currency)
                    : t('cpPayments.noDebt')}
                </div>
              </div>
              <div style={summaryStats}>
                <div>{t('cpPayments.purchased')}: {formatMoney(summary.supplier.purchasesTotal, currency)}</div>
                <div>{t('cpPayments.paid')}: {formatMoney(summary.supplier.paidOut, currency)}</div>
              </div>
            </div>
          )}
          {isWholesale && (
            <div style={{ ...summaryRow, marginTop: isSupplier ? 12 : 0, paddingTop: isSupplier ? 12 : 0, borderTop: isSupplier ? '1px solid var(--border-subtle)' : 'none' }}>
              <div>
                <div style={summaryLabel}>{t('cpPayments.wholesaleSection')}</div>
                <div style={summaryDebt(summary.wholesale.debt)}>
                  {summary.wholesale.debt > 0
                    ? t('cpPayments.theyOwe') + ' ' + formatMoney(summary.wholesale.debt, currency)
                    : t('cpPayments.noDebt')}
                </div>
              </div>
              <div style={summaryStats}>
                <div>{t('cpPayments.shipped')}: {formatMoney(summary.wholesale.salesTotal, currency)}</div>
                <div>{t('cpPayments.received')}: {formatMoney(summary.wholesale.paidIn, currency)}</div>
              </div>
            </div>
          )}
        </div>

        {/* Кнопки действий */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          {isSupplier && (
            <button
              onClick={() => setPaymentDirection('outgoing')}
              className="btn btn-primary"
              style={{ flex: 1 }}
            >
              <Icon name="wallet" size={14} />
              <span style={{ marginLeft: 4 }}>{t('cpPayments.payToSupplier')}</span>
            </button>
          )}
          {isWholesale && (
            <button
              onClick={() => setPaymentDirection('incoming')}
              className="btn btn-primary"
              style={{ flex: 1 }}
            >
              <Icon name="wallet" size={14} />
              <span style={{ marginLeft: 4 }}>{t('cpPayments.receiveFromClient')}</span>
            </button>
          )}
        </div>
        {isWholesale && isModuleOn(myShop, 'wholesale') && (
          <button
            onClick={() => navigate(`/wholesale-sale?client=${cp.id}`)}
            className="btn btn-secondary"
            style={{ width: '100%', marginBottom: 16 }}
          >
            <Icon name="truck" size={14} />
            <span style={{ marginLeft: 4 }}>{t('wholesale.newShipment')}</span>
          </button>
        )}

        {/* Табы */}
        <div style={{
          display: 'flex', gap: 4, padding: 4,
          background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)',
          marginBottom: 12,
        }}>
          {[
            { val: 'data',       label: t('counterparties.tabData') },
            { val: 'operations', label: t('counterparties.tabOperations') },
            { val: 'payments',   label: t('counterparties.tabPayments') },
          ].map(item => (
            <button
              key={item.val}
              onClick={() => setTab(item.val)}
              style={{
                flex: 1, padding: '8px 6px',
                background: tab === item.val ? 'var(--bg-elevated)' : 'transparent',
                border: 'none', borderRadius: 'var(--r-xs)',
                color: tab === item.val ? 'var(--fg)' : 'var(--fg-muted)',
                fontSize: 12, fontWeight: 700,
                cursor: 'pointer',
                boxShadow: tab === item.val ? 'var(--shadow-xs)' : 'none',
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* ──── Вкладка ДАННЫЕ ─────────────────────────────────────── */}
        {tab === 'data' && (
          <div>
            <div className="card" style={{ marginBottom: 12 }}>
              {cp.phone && <DataRow icon="users" label={t('counterparties.phone')} value={cp.phone} />}
              {cp.whatsapp && <DataRow icon="whatsapp" label={t('counterparties.whatsapp')} value={cp.whatsapp} />}
              {cp.email && <DataRow icon="copy" label={t('counterparties.email')} value={cp.email} />}
              {cp.address && <DataRow icon="store" label={t('counterparties.address')} value={cp.address} />}
              {cp.notes && <DataRow icon="info" label={t('counterparties.notes')} value={cp.notes} />}
              {!cp.phone && !cp.whatsapp && !cp.email && !cp.address && !cp.notes && (
                <p style={{ fontSize: 13, color: 'var(--fg-muted)', textAlign: 'center', padding: 16 }}>
                  {t('counterparties.noContactData')}
                </p>
              )}
            </div>
            <button
              onClick={() => setEditOpen(true)}
              className="btn btn-secondary"
              style={{ width: '100%', marginBottom: 8 }}
            >
              <Icon name="edit" size={14} />
              <span style={{ marginLeft: 4 }}>{t('common.edit')}</span>
            </button>
            <button
              onClick={async () => {
                const ok = await confirmDelete({
                  title: t('counterparties.deleteTitle', 'Удалить контрагента?'),
                  message: t('counterparties.confirmDelete', { name: cp.name }),
                });
                if (!ok) return;
                try {
                  await cpApi.remove(cp.id);
                  navigate('/counterparties');
                } catch (e) {
                  show(e.message || t('common.error'), 'error');
                }
              }}
              className="btn btn-ghost"
              style={{ width: '100%', color: 'var(--danger)' }}
            >
              <Icon name="trash" size={14} />
              <span style={{ marginLeft: 4 }}>{t('common.delete')}</span>
            </button>
          </div>
        )}

        {/* ──── Вкладка ОПЕРАЦИИ ──────────────────────────────────── */}
        {tab === 'operations' && (
          <div>
            {operations.length === 0 ? (
              <div className="empty-state">
                <div className="icon"><Icon name="package" size={28} stroke={1.5} /></div>
                <p>{t('cpPayments.noOperations')}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {operations.map(op => {
                  const isWholesale = op.kind === 'wholesale';
                  // Приходная накладная — ОДНА строка-документ (а не поштучно)
                  const isPurchaseInv = op.kind === 'purchaseInvoice';
                  const clickable = isWholesale || isPurchaseInv;
                  return (
                    <div
                      key={op.id + op.kind}
                      onClick={() => {
                        if (isWholesale) navigate(`/wholesale-sale/${op.id}`);
                        else if (isPurchaseInv) navigate(`/purchase/${op.id}`);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 14px', borderRadius: 'var(--r-sm)',
                        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                        cursor: clickable ? 'pointer' : 'default',
                      }}
                    >
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: isWholesale ? 'var(--success-soft)' : 'var(--brand-primary-soft)',
                        color:      isWholesale ? 'var(--success)'      : 'var(--brand-primary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Icon name={isWholesale ? 'truck' : (isPurchaseInv ? 'download' : 'package')} size={18} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {isPurchaseInv ? (
                          <>
                            <p style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t('purchase.invoiceLabel', { number: op.number })}
                            </p>
                            <p style={{ fontSize: 11, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {op.itemsCount} {t('wholesale.itemsCount')} · {new Date(op.createdAt).toLocaleDateString()}
                            </p>
                            {op.note && (
                              <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {op.note}
                              </p>
                            )}
                          </>
                        ) : isWholesale ? (
                          <>
                            <p style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t('wholesale.invoiceLabel', { number: op.number })}
                            </p>
                            <p style={{ fontSize: 11, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {op.itemsCount} {t('wholesale.itemsCount')} · {new Date(op.createdAt).toLocaleDateString()}
                            </p>
                            {op.itemsPreview && (
                              <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {op.itemsPreview}
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <p style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {op.productName || '—'}
                              {op.variantName && (
                                <span style={{ fontSize: 11, color: 'var(--fg-faint)', marginLeft: 6, fontWeight: 400 }}>
                                  {op.variantName}
                                </span>
                              )}
                            </p>
                            <p style={{ fontSize: 11, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {op.qty} {op.unit} · {new Date(op.createdAt).toLocaleDateString()}
                            </p>
                            {op.reason && (
                              <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {op.reason}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                      <p style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                        {formatMoney(op.money, currency)}
                      </p>
                      {isWholesale && (
                        <Icon name="chevronRight" size={14} color="var(--fg-faint)" style={{ flexShrink: 0 }} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ──── Вкладка ПЛАТЕЖИ ──────────────────────────────────── */}
        {tab === 'payments' && (
          <div>
            {payments.length === 0 ? (
              <div className="empty-state">
                <div className="icon"><Icon name="wallet" size={28} stroke={1.5} /></div>
                <p>{t('cpPayments.noPayments')}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {payments.map(p => {
                  const outgoing = p.direction === 'outgoing';
                  return (
                    <div
                      key={p.id}
                      onClick={() => onDeletePayment(p.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 14px', borderRadius: 'var(--r-sm)',
                        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: outgoing ? 'var(--danger-soft)' : 'var(--success-soft)',
                        color:      outgoing ? 'var(--danger)'      : 'var(--success)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Icon name={outgoing ? 'minus' : 'plus'} size={18} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {outgoing ? t('cpPayments.dirOutgoing') : t('cpPayments.dirIncoming')}
                        </p>
                        <p style={{ fontSize: 11, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t(`cpPayments.method_${p.method}`)} · {new Date(p.createdAt).toLocaleDateString()}
                        </p>
                        {p.note && (
                          <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.note}
                          </p>
                        )}
                        {/* Статус синхронизации: деньги требуют подтверждения второй стороной */}
                        {p.syncStatus === 'pending' && (
                          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--warning, #F59E0B)', marginTop: 2 }}>
                            {t('payReq.notConfirmed')}
                          </p>
                        )}
                        {p.syncStatus === 'declined' && (
                          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--danger)', marginTop: 2 }}>
                            {t('payReq.wasDeclined')}
                          </p>
                        )}
                        {p.syncStatus === 'confirmed' && (
                          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--success)', marginTop: 2 }}>
                            ✓ {t('payReq.isConfirmed')}
                          </p>
                        )}
                      </div>
                      <p style={{
                        fontSize: 14, fontWeight: 700,
                        color: outgoing ? 'var(--danger)' : 'var(--success)',
                        fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                      }}>
                        {outgoing ? '−' : '+'}{formatMoney(p.amount, currency)}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div style={{ height: 40 }} />
      </div>

      <CounterpartyEditSheet
        open={editOpen}
        counterparty={cp}
        onClose={() => setEditOpen(false)}
        onSaved={onEditSaved}
      />

      <PaymentSheet
        open={!!paymentDirection}
        counterpartyId={cp.id}
        counterpartyName={cp.name}
        direction={paymentDirection}
        suggestedAmount={
          paymentDirection === 'outgoing' ? summary.supplier.debt :
          paymentDirection === 'incoming' ? summary.wholesale.debt : 0
        }
        onClose={() => setPaymentDirection(null)}
        onSaved={onPaymentSaved}
      />

      <BottomNav items={navItems} />
    </div>
  );
}

function DataRow({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0' }}>
      <Icon name={icon} size={16} color="var(--fg-muted)" style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 11, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </p>
        <p style={{ fontSize: 14, fontWeight: 500, wordBreak: 'break-word' }}>{value}</p>
      </div>
    </div>
  );
}

const badgeStyle = (color, bg) => ({
  fontSize: 10, fontWeight: 700,
  color, background: bg,
  padding: '3px 8px', borderRadius: 4,
  textTransform: 'uppercase', letterSpacing: '0.04em',
});

const summaryCardStyle = {
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-md)',
  padding: 16,
  marginBottom: 12,
};
const summaryRow = {
  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
};
const summaryLabel = {
  fontSize: 10, fontWeight: 700, color: 'var(--fg-muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
};
const summaryDebt = (debt) => ({
  fontSize: 18, fontWeight: 800,
  color: debt > 0 ? 'var(--danger)' : 'var(--success)',
  fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)',
});
const summaryStats = {
  fontSize: 12, color: 'var(--fg-muted)', textAlign: 'right', lineHeight: 1.6,
  fontVariantNumeric: 'tabular-nums',
};

const iconBox = (color, bg) => ({
  width: 32, height: 32,
  borderRadius: 'var(--r-sm)',
  background: bg, color,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
});
